/**
 * Get it built — the landing page (public). A business asks Twasta builders
 * for estimates; this page explains how it works and what is paid, from the
 * instance admin's standard terms (never literals), and lists the signed-in
 * person's own requests. Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fmtDate, getPublicBuildTerms, mktError, money, type BuildExampleRow, type BuildTerms } from '@/api/marketplace'
import { listMyRequests, type BuildRequest } from '@/api/marketplaceBuild'
import { useAuth } from '@/auth/AuthContext'
import MarketplaceShell, { Check } from '../MarketplaceShell'
import HostCallout from './HostCallout'
import PaymentStages from './PaymentStages'
import { collectText, exampleAmount, noReplyText, requestStatus, termsSentence } from './clientShared'

const COMPARE: { k: string; a: string; b: string }[] = [
  { k: 'Getting a fair price', a: 'Every bid guesses the scope differently.', b: 'Everyone prices the same AI-drafted brief, item by item.' },
  { k: 'Seeing progress', a: 'Screenshots and status emails.', b: 'A working copy of the real app you can try at any time.' },
  { k: 'Checking quality', a: 'You test it yourself.', b: 'Each stage arrives with Twasta’s own test results attached.' },
  { k: 'Hosting and running it', a: 'You find a server and someone to run it.', b: 'Installed in your organization like any Marketplace app. Nothing to set up.' },
  { k: 'Your data', a: 'Often sits on the developer’s servers.', b: 'Your own private database. The builder works with sample data.' },
  { k: 'After launch', a: 'A separate maintenance deal, if you can get one.', b: 'Updates and fixes are part of the monthly price.' },
]

export default function GetItBuiltPage() {
  const { authStatus } = useAuth()
  const authed = authStatus === 'authed'

  const [terms, setTerms] = useState<BuildTerms | null>(null)
  const [currency, setCurrency] = useState('INR')
  const [example, setExample] = useState<BuildExampleRow[]>([])
  const [exampleMinor, setExampleMinor] = useState(0)
  const [termsError, setTermsError] = useState<string | null>(null)

  const [requests, setRequests] = useState<BuildRequest[] | null>(null)
  const [reqError, setReqError] = useState<string | null>(null)
  const [open, setOpen] = useState(0)

  const loadTerms = useCallback(async () => {
    setTermsError(null)
    try {
      const base = await getPublicBuildTerms()
      setTerms(base.terms)
      setCurrency(base.currency)
      const ex = exampleAmount(base.terms, base.currency)
      setExampleMinor(ex)
      if (ex > 0) {
        const withEx = await getPublicBuildTerms(ex)
        setExample(withEx.example)
      }
    } catch (e) {
      setTermsError(mktError(e, 'The payment terms could not be loaded.').message)
    }
  }, [])

  const loadRequests = useCallback(async () => {
    setReqError(null)
    try {
      const r = await listMyRequests()
      setRequests(r.requests)
    } catch (e) {
      setReqError(mktError(e, 'Your requests could not be loaded.').message)
    }
  }, [])

  useEffect(() => { loadTerms() }, [loadTerms])
  useEffect(() => { if (authed) loadRequests() }, [authed, loadRequests])

  const advancePct = terms?.stages[0]?.pct
  const steps = [
    { t: 'Describe it', d: 'Twasta AI turns your words into a brief every builder prices the same way.' },
    { t: 'Compare estimates', d: 'Price to build, monthly price to use, time, and what each one covers.' },
    { t: 'Place the order', d: advancePct !== undefined ? `Pay Twasta a ${advancePct}% advance from your wallet.` : 'Pay Twasta the advance from your wallet.' },
    { t: 'Watch it being built', d: 'Try the builder’s working copy with sample data whenever you like.' },
    { t: 'Accept & install', d: 'Sign off the last stage. The app is installed in your organization.' },
    { t: 'Use it monthly', d: 'It sits in My apps. The builder keeps it updated.' },
  ]

  const faqs = [
    { q: 'Where is the app built, and where does it run?', a: 'The builder builds it in their own Twasta account. When you accept the last stage it is installed in your organization exactly like a Marketplace app: your own private copy, your own database, and it appears in My apps.' },
    { q: 'Who can see our data?', a: 'Only the people you invite. While the app is being built you review it with sample data. Your real records go into your own copy after it is installed — the builder never sees them unless you allow support access.' },
    { q: 'What do we pay after it is built?', a: 'The monthly price from the estimate you accepted — per person or per server — taken from your organization wallet. It covers hosting, updates and fixes, the same way every Marketplace app is paid for.' },
    {
      q: 'How does paying for the build work?',
      a: terms
        ? `You pay Twasta, never the builder directly. Twasta’s standard terms: ${termsSentence(terms)}. ${collectText(terms)}, and Twasta pays the builder only after you approve it. ${noReplyText(terms)}`
        : 'You pay Twasta, never the builder directly, in Twasta’s standard stages. Twasta pays the builder for a stage only after you approve it.',
    },
    { q: 'Can the builder sell our app to other companies?', a: 'Only if you allow it when you order — you usually pay less to build in return. Your data and your settings never go with it.' },
    { q: 'What if the builder stops supporting the app?', a: 'Your copy keeps running and you can always download all your data. Twasta helps you move the app to another builder.' },
  ]

  const latest = requests?.find((r) => r.status === 'open') || requests?.[0] || null

  return (
    <MarketplaceShell>
      <section className="mkt-hero" style={{ textAlign: 'left' }}>
        <div className="mkt-wrap" style={{ display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'center', paddingTop: 52, paddingBottom: 48 }}>
          <div style={{ flex: '1 1 520px', display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
            <span className="mkt-kicker" style={{ alignSelf: 'flex-start' }}>Get it built</span>
            <h1 style={{ fontSize: 'clamp(30px, 5vw, 44px)', lineHeight: 1.12 }}>Have an app built for your business by a Twasta builder.</h1>
            <p className="mkt-muted" style={{ fontSize: 18, maxWidth: 600, margin: 0 }}>
              Describe what you need. Builders send you estimates: a price to build it, and a monthly price to use it. They build it on Twasta, and when you accept the work it is installed in your organization — just like any app from the Marketplace.
            </p>
            <div className="mkt-row">
              <Link to="/marketplace/build/new" className="btn btn-primary btn-lg">Ask for estimates</Link>
              <button type="button" className="btn btn-secondary btn-lg" onClick={() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })}>How it works</button>
            </div>
            <div className="mkt-ticks" style={{ justifyContent: 'flex-start' }}>
              {['Free to ask', 'Twasta pays the builder only after you approve', 'Your own private copy', 'Your data stays yours'].map((t) => (
                <span key={t}><Check />{t}</span>
              ))}
            </div>
          </div>
          <div style={{ flex: '1 1 380px', maxWidth: 460 }}>
            {authed && latest ? (
              <Link to={latest.status === 'draft' ? `/marketplace/build/new/${latest.id}` : `/marketplace/build/requests/${latest.id}`} className="mkt-card" style={{ boxShadow: '0 12px 32px var(--color-shadow, rgba(0,0,0,0.08))' }}>
                <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                  <strong>{latest.title}</strong>
                  <span className={`mkt-pill ${requestStatus(latest).tone}`}>{requestStatus(latest).label}</span>
                </div>
                <p>{latest.description || 'No description yet.'}</p>
                <div className="mkt-card-foot">
                  <span>{latest.status === 'draft' ? 'Draft' : `${latest.estimates_count} ${latest.estimates_count === 1 ? 'estimate' : 'estimates'}`}</span>
                  <span style={{ color: 'var(--color-accent)', fontWeight: 600 }}>{latest.status === 'draft' ? 'Finish & send →' : 'Compare estimates →'}</span>
                </div>
              </Link>
            ) : (
              <div className="mkt-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 12px 32px var(--color-shadow, rgba(0,0,0,0.08))' }}>
                <strong>How you pay for the build</strong>
                <span className="mkt-muted" style={{ fontSize: 14 }}>You pay Twasta in standard stages. Twasta pays the builder for each stage only after you approve it.</span>
                {terms ? (
                  <PaymentStages stages={terms.stages} rows={example.length ? example : undefined} amountMinor={exampleMinor} currency={currency} who="client" advanceRelease={terms.advance_release} compact />
                ) : !termsError && <span className="mkt-faint" role="status">Loading the terms…</span>}
                {terms && <span className="mkt-faint">{terms.stages.map((s) => `${s.name} ${s.pct}%`).join(' · ')}</span>}
              </div>
            )}
          </div>
        </div>
      </section>

      {authed && (
        <section className="mkt-wrap" style={{ paddingTop: 40, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <h2 style={{ fontSize: 22 }}>Your requests</h2>
            <Link to="/marketplace/my-apps" style={{ fontSize: 14 }}>Everything in My apps →</Link>
          </div>
          {reqError ? (
            <div className="mkt-banner bad" role="alert"><div><strong>Your requests didn’t load</strong>{reqError}</div><button type="button" className="btn btn-secondary btn-sm" onClick={loadRequests}>Try again</button></div>
          ) : requests === null ? (
            <span className="mkt-muted" role="status">Loading your requests…</span>
          ) : requests.length === 0 ? (
            <div className="mkt-empty">
              <strong>You haven’t asked for any estimates yet</strong>
              <span className="mkt-muted">Describe the app you need and Twasta builders will send you estimates.</span>
              <Link to="/marketplace/build/new" className="btn btn-primary">Ask for estimates</Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
              {requests.map((r) => {
                const st = requestStatus(r)
                const draft = r.status === 'draft'
                const statusLabel = draft ? st.label : r.status === 'open' ? `${r.estimates_count} ${r.estimates_count === 1 ? 'estimate' : 'estimates'}` : st.label
                return (
                  <Link key={r.id} className="mkt-card" to={draft ? `/marketplace/build/new/${r.id}` : `/marketplace/build/requests/${r.id}`}>
                    <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                      <strong style={{ fontSize: 17 }}>{r.title}</strong>
                      <span className={`mkt-pill ${draft ? '' : r.status === 'open' ? (r.estimates_count ? 'ok' : 'info') : st.tone}`}>{statusLabel}</span>
                    </div>
                    <p>{r.description ? (r.description.length > 160 ? `${r.description.slice(0, 157)}…` : r.description) : 'No description yet.'}</p>
                    <div className="mkt-card-foot">
                      <span>{r.posted_at ? `Posted ${fmtDate(r.posted_at)}` : `Saved ${fmtDate(r.created_at)}`}</span>
                      <span style={{ color: 'var(--color-accent)', fontWeight: 600, fontSize: 14 }}>
                        {draft ? 'Finish & send' : r.status === 'open' ? 'Compare estimates' : 'Open'} →
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </section>
      )}

      <section id="how" className="mkt-wrap" style={{ paddingTop: 52, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <h2 style={{ fontSize: 26 }}>How it works</h2>
          <p className="mkt-muted" style={{ margin: 0 }}>The same steps as any outsourcing site — except the app is built on Twasta and delivered to you through the Marketplace.</p>
        </div>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 }}>
          {steps.map((s, i) => (
            <li key={s.t} className="mkt-panel" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 999, background: 'var(--color-accent-weak)', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{i + 1}</span>
              <strong style={{ fontSize: 16 }}>{s.t}</strong>
              <span className="mkt-muted" style={{ fontSize: 14 }}>{s.d}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="mkt-wrap" style={{ paddingTop: 44 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          <div className="mkt-panel" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="mkt-pill info" style={{ alignSelf: 'flex-start' }}>1 · To build it</span>
            <strong style={{ fontSize: 20 }}>{advancePct !== undefined ? `${advancePct}% when you order, then stage by stage` : 'An advance when you order, then stage by stage'}</strong>
            <p className="mkt-muted" style={{ margin: 0 }}>
              {terms
                ? <>You pay Twasta — never the builder directly: {termsSentence(terms)}. {collectText(terms)}, and Twasta pays the builder only after you approve it.</>
                : 'You pay Twasta — never the builder directly — in Twasta’s standard stages, and Twasta pays the builder only after you approve each one.'}
            </p>
          </div>
          <div className="mkt-panel" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="mkt-pill ok" style={{ alignSelf: 'flex-start' }}>2 · To use it</span>
            <strong style={{ fontSize: 20 }}>A monthly price, like any Marketplace app</strong>
            <p className="mkt-muted" style={{ margin: 0 }}>From the day it is installed: per person or per server, taken from your wallet. Hosting, updates and fixes are included, and you can change the plan or cancel from My apps.</p>
            <HostCallout currency={currency} />
          </div>
        </div>
      </section>

      <section className="mkt-wrap" style={{ paddingTop: 28 }}>
        <div className="mkt-panel" style={{ padding: 26, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <h2 style={{ fontSize: 22 }}>How the build is paid</h2>
            <p className="mkt-muted" style={{ fontSize: 14, margin: 0 }}>
              Twasta’s standard payment terms{exampleMinor > 0 ? <>, shown on an example {money(exampleMinor, currency)} estimate</> : ''}.
            </p>
          </div>
          {termsError ? (
            <div className="mkt-banner bad" role="alert"><div><strong>The payment terms didn’t load</strong>{termsError}</div><button type="button" className="btn btn-secondary btn-sm" onClick={loadTerms}>Try again</button></div>
          ) : terms ? (
            <PaymentStages stages={terms.stages} rows={example.length ? example : undefined} amountMinor={exampleMinor} currency={currency} who="client" advanceRelease={terms.advance_release} />
          ) : (
            <span className="mkt-muted" role="status">Loading the terms…</span>
          )}
          {terms && <span className="mkt-faint">{noReplyText(terms)} {terms.client_fee_percent ? `Twasta adds a ${terms.client_fee_percent}% service fee to each share.` : ''}</span>}
        </div>
      </section>

      <section className="mkt-wrap" style={{ paddingTop: 44 }}>
        <div className="mkt-panel" style={{ padding: 26, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <h2 style={{ fontSize: 22 }}>Why have it built on Twasta</h2>
          <div className="mkt-scroll">
            <table className="mkt-table" style={{ minWidth: 720, fontSize: 15 }}>
              <thead>
                <tr>
                  <th style={{ width: '22%' }}><span className="mkt-sr">Topic</span></th>
                  <th>Typical outsourcing site</th>
                  <th style={{ color: 'var(--color-accent)' }}>Get it built on Twasta</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((c) => (
                  <tr key={c.k}>
                    <td style={{ fontWeight: 600 }}>{c.k}</td>
                    <td className="mkt-muted">{c.a}</td>
                    <td>
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ color: 'var(--color-success)', marginTop: 2 }}><Check size={18} /></span>{c.b}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="mkt-wrap" style={{ paddingTop: 44, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 900 }}>
        <h2 style={{ fontSize: 24 }}>Questions people ask</h2>
        <div className="mkt-panel" style={{ padding: '4px 20px' }}>
          {faqs.map((f, i) => {
            const isOpen = open === i
            const id = `faq-${i}`
            return (
              <div key={f.q} style={{ borderBottom: i < faqs.length - 1 ? '1px solid var(--color-border)' : 0 }}>
                <button type="button" aria-expanded={isOpen} aria-controls={id} onClick={() => setOpen(isOpen ? -1 : i)}
                  style={{ width: '100%', textAlign: 'left', font: 'inherit', fontSize: 16, fontWeight: 600, color: 'var(--color-text)', background: 'transparent', border: 0, padding: '18px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, cursor: 'pointer', minHeight: 44 }}>
                  <span>{f.q}</span>
                  <span aria-hidden="true" className="mkt-muted" style={{ fontSize: 22 }}>{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && <p id={id} className="mkt-muted" style={{ padding: '0 4px 18px', fontSize: 15, margin: 0 }}>{f.a}</p>}
              </div>
            )
          })}
        </div>
      </section>

      <section className="mkt-wrap" style={{ paddingTop: 44, paddingBottom: 56 }}>
        <div className="mkt-dark" style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong style={{ fontSize: 22 }}>Do you build on Twasta?</strong>
            <span className="mkt-muted">Businesses are asking for apps right now. Build them in your own Twasta account, get paid stage by stage, and earn every month while they use it.</span>
          </div>
          <Link to="/marketplace/work" className="btn btn-secondary btn-lg">Find work</Link>
        </div>
      </section>
    </MarketplaceShell>
  )
}
